import { LonLatValueType } from 'app/form_config/helpers';
import { z, ZodError } from 'zod';
import { round } from 'lodash';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';

export function convertMPStoKMPH(value?: number | string | null) {
    if (value == null) {
        return null;
    }
    const parsedValue = z.coerce.number().parse(value);
    return (parsedValue * 3.6).toFixed(1);
}

export function convertMetersPerSecondToKmPerHour(value: number): number {
    return value * 3.6;
}

function latLonValid(latLon: LonLatValueType): boolean {
    const schema = z.array(z.number()).min(2).max(2);
    const result = schema.safeParse(latLon);
    return result.success;
}

export function roundLatLon(latLon: LonLatValueType): LonLatValueType {
    return latLon.map((value) => round(value, 6)) as LonLatValueType;
}

export function roundMapLocation(location: MapLocation): MapLocation {
    return { latitude: round(location.latitude, 6), longitude: round(location.longitude, 6) };
}

export function areMapLocationsEqual(
    pointA?: MapLocation | null,
    pointB?: MapLocation | null,
): boolean {
    if (!pointA && !pointB) {
        return true;
    }
    if (!pointA || !pointB) {
        return false;
    }

    return (
        roundMapLocation(pointA).latitude == roundMapLocation(pointB).latitude &&
        roundMapLocation(pointA).longitude == roundMapLocation(pointB).longitude
    );
}

export function areLatLonsEqual(pointA: LonLatValueType, pointB: LonLatValueType): boolean {
    if (!pointA && !pointB) {
        return true;
    }
    if (!pointA || !pointB) {
        return false;
    }
    const pointAValid = latLonValid(pointA);
    const pointBValid = latLonValid(pointB);
    if (!pointAValid && !pointBValid) {
        return true;
    }
    if (!pointAValid || !pointBValid) {
        return false;
    }

    return (
        pointA[0].toFixed(6) == pointB[0].toFixed(6) && pointA[1].toFixed(6) == pointB[1].toFixed(6)
    );
}

export function areShapesEqual(shapeA?: LonLatValueType[], shapeB?: LonLatValueType[]): boolean {
    if (!shapeA && !shapeB) {
        return true;
    }
    if (!shapeA || !shapeB) {
        return false;
    }
    if (shapeA.length != shapeB.length) {
        return false;
    }
    let result = true;
    for (let i = 0; i < shapeA.length; i++) {
        if (!areLatLonsEqual(shapeA[i], shapeB[i])) {
            result = false;
        }
    }
    return result;
}

export function groupBy<T>(array: T[], key: keyof T): T[][] {
    // Create a Set of unique key values
    const uniqueKeys = new Set(array.map((item) => item[key]));

    // Map each unique key to a filtered array of items that have that key value
    return Array.from(uniqueKeys).map((uniqueKey) => {
        return array.filter((item) => item[key] === uniqueKey);
    });
}

export function groupByKey<T, K extends keyof T>(items: T[], key: K): Record<string, T[]> {
    return items.reduce((grouped: Record<string, T[]>, item) => {
        const group = String(item[key]);
        if (!(group in grouped)) grouped[group] = [];
        grouped[group].push(item);
        return grouped;
    }, {});
}

export function handleZodParseError(error: unknown) {
    if (error instanceof ZodError) {
        console.error(error.message);
    } else {
        console.error(error);
    }
}

// format does in an iso-8601 like way, but we retain the timezone data
